import { ChangeDetectorRef, Component, SimpleChanges } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { SharedModule } from 'src/app/layouts/full/shared/shared.module';
import { Client } from 'src/app/models/client.model';
import { Clinic } from 'src/app/models/clinic.model';
import { User } from 'src/app/models/user.model';
import { ClientsService } from 'src/app/services/clients.service';
import { ClinicService } from 'src/app/services/clinic.service';
import { ModalCreateClientService } from 'src/app/services/modal-create-client.service';
import { UserService } from 'src/app/services/user.service';
import Swal from 'sweetalert2';
import { v4 as uuidv4 } from 'uuid';

@Component({
  selector: 'app-users',
  standalone: true,
  imports: [SharedModule],
  templateUrl: './users.component.html',
  styleUrl: './users.component.scss',
  providers: [MessageService]
})
export class UsersComponent {
  clinics: Clinic[]

  public user: User
  public client: Client
  public createUserForm: FormGroup

  constructor(
    private userService: UserService,
    private clinicService: ClinicService,
    public modalCreateClientService: ModalCreateClientService,
    public clientService: ClientsService,
    private formBuilder: FormBuilder,
    private messageService: MessageService,
    private cd: ChangeDetectorRef
  ) {
    this.user = userService.user

    this.createUserForm = this.formBuilder.group({
      fullName: ['', [Validators.required, Validators.minLength(1)]],
      email: ['', [Validators.required, Validators.email]],
      password: ['', [Validators.required, Validators.minLength(2)]],
      clinics: [[], Validators.required]
    })
  }

  users: User[] = []
  clientUsers: User[] = []
  filteredClientUsers: User[] = []
  selectedUser: User
  selectedClinics: Clinic[] = []
  loading: boolean = false
  sidebarVisible: boolean = false;
  allPermissions: string[] = ['design', 'orders', 'scan']
  assignedClinicsModalVisible: boolean = false
  createUserModalVisible: boolean = false
  allClinicsSelected: boolean = false

  ngOnInit(): void {
    let getClientDTO = this.user.email

    // Si es role_customer o role_clinic busca el client adecuado (desde el back le añadimos el user la propiedad client)
    if (this.user && this.user.client) {
      getClientDTO = this.user.client.email
    }
    this.clientService.getClient(getClientDTO).subscribe((client) => {
      this.client = client
      console.log("This client in users: ", this.client)
      this.clinicService.getClinicsByClientId(this.client.ID).subscribe({
        next: (clinics) => {
          this.clinics = clinics
          this.listUsers()
        },
        error: (err) => {
          console.error("Error getting clinics: ", err)
        },
      })
    })
  }

  listUsers(): void {
    this.loading = true;

    this.clientUsers = [];

    if (this.client.users && Array.isArray(this.client.users)) {

      // Creamos un array para almacenar las promesas
      const userPromises = [];

      this.client.users.forEach((userId) => {

        // Obtiene la información del usuario
        const userPromise = new Promise<void>((resolve, reject) => {
          this.userService.getUserById(userId).subscribe({
            next: (userResponse) => {

              const clientUser = userResponse;

              // Verifica si el usuario ya existe en la lista de usuarios
              if (!this.clientUsers.some(existingUser => existingUser.ID === clientUser.ID)) {

                // Si el usuario no está en la lista, lo agrega
                // Obtiene los permisos del usuario
                this.userService.getUserPermissionsById(clientUser.ID).subscribe({
                  next: (permissions) => {

                    clientUser.permissions = permissions || [];

                    // Procesa las clínicas asignadas
                    if (clientUser.assignedClinics) {
                      //@ts-expect-error
                      clientUser.assignedClinics = clientUser.assignedClinics.map((clinicID) => {

                        const clinic = this.clinics.find((clinic) => clinic.ID === clinicID);

                        if (!clinic) {
                          console.warn(`No clinic found for ID: ${clinicID}`);
                        }

                        return clinic;
                      });
                    }

                    // Agrega el usuario procesado a la lista de usuarios del cliente
                    this.clientUsers.push(clientUser);

                    resolve(); // Resuelve la promesa cuando el usuario es procesado
                  },
                  error: (error) => {
                    console.error(`Error fetching permissions for user ID ${clientUser.ID}:`, error);
                    reject(error); // Rechaza la promesa si hay un error
                  }
                });
              } else {
                resolve(); // Resuelve la promesa incluso si el usuario ya está en la lista
              }
            },
            error: (error) => {
              console.error(`Error fetching user by ID ${userId}:`, error);
              reject(error); // Rechaza la promesa si hay un error
            }
          });
        });

        userPromises.push(userPromise); // Añade la promesa a la lista
      });

      // Usamos Promise.all para esperar a que todas las promesas se resuelvan
      Promise.all(userPromises)
        .then(() => {
          console.log("Client users:", this.clientUsers)
          this.filteredClientUsers = this.clientUsers
          this.loading = false;
          this.cd.detectChanges(); // Asegura que Angular actualice la vista
        })
        .catch((error) => {
          console.error("Error in one of the requests:", error);
          this.loading = false;
          this.cd.detectChanges(); // Asegura que Angular actualice la vista en caso de error
        });
    } else {
      this.clientUsers = [];
      this.loading = false;
    }
  }

  isPermissionSelected(permission: string): boolean {
    return this.selectedUser?.permissions?.includes(permission);
  }

  togglePermissions(permission: string, event: any) {
    if (event.target.checked) {
      if (!this.selectedUser.permissions.includes(permission)) {
        this.selectedUser.permissions.push(permission);
      }
    } else {
      this.selectedUser.permissions = this.selectedUser.permissions.filter(perm => perm !== permission);
    }
  }

  toggleClinicSelection(clinic: Clinic, event: any) {
    const isChecked = event.target.checked;

    if (isChecked) {
      if (!this.selectedClinics.some(c => c.ID === clinic.ID)) {
        this.selectedClinics.push(clinic);
      }
    } else {
      this.selectedClinics = this.selectedClinics.filter(c => c.ID !== clinic.ID);
    }

    this.updateAssignedClinics()
    //@ts-expect-error
    this.selectedUser.assignedClinics = this.selectedClinics;
  }

  toggleAllClinicsSelection(event: any): void {
    const isChecked = event.target.checked;

    if (isChecked) {
      //@ts-expect-error
      this.selectedUser.assignedClinics = [...this.clinics];
      this.selectedClinics = [...this.clinics];

      this.userService.updateAllClinicsAssigned(this.selectedUser.ID, true).subscribe({
        next: () => {
          this.selectedUser.allClinicsAssigned = true
          console.log("AllClinicsAssigned updated succesfully.")
        },
        error: (error) => {
          Swal.fire('Error updating AllClinicsAssigned:', error.error.message, 'error');
        }
      })
    } else {
      this.selectedUser.assignedClinics = [];
      this.selectedClinics = [];

      this.userService.updateAllClinicsAssigned(this.selectedUser.ID, false).subscribe({
        next: () => {
          this.selectedUser.allClinicsAssigned = false
          console.log("AllClinicsAssigned updated succesfully.")
        },
        error: (error) => {
          Swal.fire('Error updating AllClinicsAssigned:', error.error.message, 'error');
        }
      })
    }

    this.updateAssignedClinics();
    this.assignedClinicsModalVisible = false
  }

  areAllClinicsSelected(): boolean {
    return this.clinics.every(clinic => this.isClinicSelected(clinic));
  }

  isClinicSelected(clinic: Clinic): boolean {
    if (!this.selectedClinics) {
      this.selectedClinics = []
      return false;
    }
    return this.selectedClinics.some(c => c.ID === clinic.ID);
  }

  update(user: User) {

    Swal.fire({
      title: 'Please wait',
      html: '<div>Updating user permissions...</div><br>',
      allowOutsideClick: false,
      showConfirmButton: false,
      allowEscapeKey: false,
    });

    this.userService.update(user.ID, user.permissions).subscribe((resp) => {
      Swal.fire({
        icon: 'success',
        title: 'User permissions updated',
        showConfirmButton: false,
        timer: 1500
      })
      this.listUsers()
      this.sidebarVisible = false
    })
  }

  toggleSidebar() {
    this.sidebarVisible = !this.sidebarVisible;
  }

  selectUserOnRowClick(user: User) {
    this.selectedUser = user
    this.sidebarVisible = true
  }

  onRowClick(event) {
    if (event.type === "click") {
      this.selectUserOnRowClick(event.row);
    }
  }

  openAssignedClinicsModal(user: User) {
    event.stopPropagation()
    console.log("Selected user: ", user)
    this.selectedUser = user

    if (this.selectedUser.allClinicsAssigned) {
      this.selectedClinics = this.clinics
    } else {
      //@ts-expect-error
      this.selectedClinics = this.selectedUser.assignedClinics
    }

    this.allClinicsSelected = this.selectedUser.allClinicsAssigned;

    this.assignedClinicsModalVisible = true
  }

  updateAssignedClinics() {
    let assignedClinics: { id: string, name: string, clientId: string }[] = []

    assignedClinics = this.selectedClinics.map((clinic) => {
      return { id: clinic.ID, name: clinic.name, clientId: clinic.clientID }
    })

    this.userService.updateAssignedClinics(this.selectedUser.ID, this.client.ID, assignedClinics).subscribe({
      next: () => {
        this.showUpdatedAssignedClinicsSuccess()
      },
      error: () => {
        this.showUpdatedAssignedClinicsError()
      }
    });
  }

  async createUser() {
    if (this.createUserForm.valid) {
      this.createUserModalVisible = false
      Swal.showLoading()

      const { fullName, email, password, clinics } = this.createUserForm.value

      const userId = uuidv4()

      const userData = {
        userId,
        name: fullName,
        surname: " ",
        email,
        password,
        role: "ROLE_CLINIC",
        assignedClinics: clinics.map((clinic) => clinic.ID)
      }

      this.userService.createClinicUser(userData).subscribe({
        next: () => {
          this.clientService.getClient(this.client.email).subscribe({
            next: (client) => {
              const clientID = client.ID
              const clientUsers = client.users || []
              const updatedUsers = [...clientUsers, userId.toString()];

              this.clientService.updateClientUsers(clientID, updatedUsers).subscribe({
                next: () => {
                  this.clientService.getClient(this.client.email).subscribe((client) => {
                    this.client = client

                    this.listUsers()
                    this.createUserForm.reset()
                    Swal.fire('User created', '', 'success');
                  })
                },
                error: (err) => {
                  console.error(err)
                }
              })

              if (this.allClinicsSelected) {
                this.userService.updateAllClinicsAssigned(userId, true).subscribe(() => {
                  console.log("AllClinicsSelected updated in new user")
                  this.allClinicsSelected = false
                })
              }
            },
            error: (error) => {
              Swal.fire('Creation of the user failed', error.error.message, 'error');
            },
          })
        },
        error: (error) => {
          this.createUserModalVisible = false
          Swal.fire('Creation of the user failed', error.error.message, 'error');
        }
      })
    } else {
      Swal.fire('Form is invalid', '', 'error');
    }
  }

  toggleSelectAll() {
    if (this.allClinicsSelected) {
      this.createUserForm.get('clinics').setValue([]);
    } else {
      this.createUserForm.get('clinics').setValue(this.clinics);
    }
    this.allClinicsSelected = !this.allClinicsSelected;
  }

  onClinicChange(event) {
    const selectedClinics = event.value;
    if (selectedClinics.length === this.clinics.length) {
      this.allClinicsSelected = true;
    } else {
      this.allClinicsSelected = false;
    }
  }

  async getClinicsByClientID(clientID: string): Promise<void> {
    try {
      await this.clinicService.getClinicsByClientId(clientID).subscribe({
        next: (clinics) => {
          this.clinics = clinics
          this.cd.detectChanges();
        },
        error: (error) => {
          console.error("Error fetching clinics: ", error);
        }
      });

    } catch (error) {
      console.error(error)
    }
  }

  async openCreateUserModal(client) {
    this.createUserForm.reset();

    if (this.user.role === 'ROLE_DISTRIBUTOR_PROFESSIONAL') {
      await this.getClinicsByClientID(client.ID);
    }

    this.createUserModalVisible = true;
  }

  resetPassword(clientUser) {
    event.stopPropagation()

    const email = clientUser.email

    this.userService.resetFirebasePassword(email).subscribe({
      next: () => {
        this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Password reset email sent successfully.' });
      },
      error: () => {
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Email not found' });
      }
    })
  }

  applyFilter(event: Event) {
    const filterValue = (event.target as HTMLInputElement).value.trim().toLowerCase();

    if (!filterValue) {
      this.filteredClientUsers = this.clientUsers; // Si no hay filtro, mostrar todas
      return;
    }

    this.filteredClientUsers = this.clientUsers.filter(user =>
      user.name.toLowerCase().includes(filterValue) ||
      user.email.toLowerCase().includes(filterValue)
    );

  }

  showUpdatedAssignedClinicsSuccess() {
    this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Assigned clinics updated successfully.' });
  }

  showUpdatedAssignedClinicsError() {
    this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Error updating assigned clinics' });
  }
}
